/* ============================================================
   SIMPLE CLIMA — Precios: MXN principal + USD de referencia
   ============================================================
   QUÉ HACE ESTE ARCHIVO
   Los productos de la tienda tienen su precio guardado en MXN
   (ese es el precio "real" que ve Shopify y Google). Este script
   agrega, JUNTO a cada precio en MXN, su equivalente aproximado
   en USD, como dato informativo para el cliente.

   Resultado visual en producto/grid:  "$6,812.65 MXN  402.00 USD"
   Resultado visual en carrito:         "$6,812.65  402.00 USD"

   DE DÓNDE SALE EL TIPO DE CAMBIO
   No está fijo en el código: se pide EN VIVO a la misma API que
   usa el Google Apps Script de precios (fawazahmed0). Así, el USD
   que se muestra aquí queda alineado con el precio MXN que el
   Apps Script calculó (ambos usan el mismo tipo de cambio).
   Si la API falla, hay una cadena de respaldos (ver TC_FALLBACK).

   IMPORTANTE — DÓNDE VIVE EL PRECIO REAL
   Este script NO cambia precios; solo MUESTRA. El precio real en
   MXN lo fija el Apps Script (fuera del tema). El USD original de
   cada producto vive en el metafield de variante
   'custom.precio_original_usd'. Ver la documentacion (seccion 7).

   DONDE APLICA
   - Pagina de producto y grid de coleccion (precio con clase
     .price-item--regular).
   - Carrito: drawer lateral, pagina /cart, y subtotales.

   NOTA SOBRE LOS NOMBRES DE CLASE (puede confundir):
   Por razones historicas (antes el sistema era USD principal +
   MXN secundario, y luego se invirtio), los nombres de clase
   quedaron "al reves" respecto a lo que muestran:
       - el <span> que dice "MXN" usa la clase .sc-usd
       - el <span> que dice "USD" usa la clase .sc-mxn
   Funciona bien (las clases son solo identificadores para evitar
   duplicar la inyeccion), pero NO te guies por el nombre de la
   clase para saber que moneda es. Se dejaron asi para no arriesgar
   romper algun CSS que dependa de esos nombres.
   ============================================================ */

(function () {
  'use strict';

  /* ── VARIABLE AJUSTABLE ──────────────────────────────────────
     Tipo de cambio de EMERGENCIA: solo se usa si las DOS APIs de
     tipo de cambio fallan. Debe mantenerse igual al TC_FALLBACK
     del Apps Script de precios para que sean coherentes. */
  var TC_FALLBACK = 20;

  /* URLs de la API de tipo de cambio (misma que el Apps Script):
     1) jsdelivr (principal)  2) Cloudflare (respaldo). */
  var URL_PRINCIPAL = 'https://cdn.jsdelivr.net/npm/@fawazahmed0/currency-api@latest/v1/currencies/usd.json';
  var URL_FALLBACK  = 'https://latest.currency-api.pages.dev/v1/currencies/usd.json';

  /* Cache del tipo de cambio para ESTA carga de pagina: se pide
     una sola vez a la API y se reutiliza para todos los precios
     (no una peticion por cada precio). */
  var tcActual = null;    // el numero una vez resuelto
  var tcPromesa = null;   // la promesa en curso (evita pedir dos veces a la vez)

  /* Obtiene el tipo de cambio USD->MXN, con cadena de respaldos:
       1. Intenta la URL principal (jsdelivr).
       2. Si falla, intenta la de Cloudflare.
       3. Si ambas fallan, usa TC_FALLBACK (hardcodeado).
     Devuelve una PROMESA que resuelve al numero. Cachea el
     resultado en 'tcActual' para no repetir la peticion. */
  function obtenerTipoCambio() {
    if (tcActual !== null) return Promise.resolve(tcActual);  // ya lo tenemos
    if (tcPromesa) return tcPromesa;                          // ya se esta pidiendo

    tcPromesa = intentar(URL_PRINCIPAL)
      .then(function (tc) {
        if (tc) return tc;                 // principal funciono
        return intentar(URL_FALLBACK);     // si no, probar Cloudflare
      })
      .then(function (tc) {
        tcActual = tc || TC_FALLBACK;      // si ninguna dio valor, usar el fijo
        return tcActual;
      })
      .catch(function () {
        tcActual = TC_FALLBACK;            // ante cualquier error, usar el fijo
        return tcActual;
      });

    return tcPromesa;
  }

  /* Pide el JSON a una URL y extrae el valor usd->mxn.
     Devuelve el numero, o null si algo falla (para poder
     encadenar el siguiente respaldo). */
  function intentar(url) {
    return fetch(url)
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (data) {
        // La API responde algo como: { "usd": { "mxn": 16.94, ... } }
        if (data && data.usd && data.usd.mxn) return data.usd.mxn;
        return null;
      })
      .catch(function () { return null; });
  }

  /* Formatea un numero como moneda mexicana con 2 decimales.
     Ej: 402 -> "402.00", 6812.6 -> "6,812.60".
     (Se formatea a mano para evitar el bug de money_without_currency
     de Shopify, que divide entre 100.) */
  function formatearMoneda(valor) {
    return valor.toLocaleString('es-MX', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  }

  /* Extrae el numero de un texto de precio.
     Ej: "$6,812.65 MXN" -> 6812.65
     Quita todo lo que no sea digito/punto/coma, y luego las comas
     de miles, para quedarse con un numero parseable. */
  function parsearPrecio(texto) {
    var limpio = texto.replace(/[^0-9.,]/g, '').replace(/,/g, '');
    return parseFloat(limpio);
  }

  /* ── PRECIO DE PRODUCTO / GRID ───────────────────────────────
     Inyecta, dentro del span del precio, la etiqueta "MXN" (en
     negrita, para aclarar la moneda del numero grande) + el
     equivalente en "USD" (en gris, calculado). */
  function inyectarEnPrecioProducto(spanPrecio, tc) {
    // Si ya inyectamos aqui antes, no repetir (evita duplicados
    // cuando el observer se dispara varias veces).
    if (spanPrecio.querySelector('.sc-usd')) return;

    var mxn = parsearPrecio(spanPrecio.textContent);
    if (isNaN(mxn) || mxn <= 0) return;   // sin numero valido, salir
    var usd = mxn / tc;                   // USD ~= MXN / tipo de cambio

    // Recorta el espacio en blanco final del texto del precio, para
    // que la etiqueta "MXN" quede pegada sin un hueco raro.
    for (var i = 0; i < spanPrecio.childNodes.length; i++) {
      var nodo = spanPrecio.childNodes[i];
      if (nodo.nodeType === 3 && nodo.nodeValue && /\d/.test(nodo.nodeValue)) {
        nodo.nodeValue = nodo.nodeValue.replace(/\s+$/, '');
      }
    }

    // Etiqueta "MXN" (negrita, negro). OJO: clase .sc-usd (ver nota
    // de nombres de clase al inicio del archivo).
    var spanMXN = document.createElement('span');
    spanMXN.className = 'sc-usd';
    spanMXN.style.cssText = 'font-size: 1em; color: rgb(0, 0, 0); font-weight: bold; margin-left: 4px;';
    spanMXN.textContent = 'MXN';

    // Equivalente en USD (gris, mas chico). OJO: clase .sc-mxn.
    var spanUSD = document.createElement('span');
    spanUSD.className = 'sc-mxn';
    spanUSD.style.cssText = 'font-size: 0.8em; color: rgb(102, 102, 102); margin-left: 8px;';
    spanUSD.textContent = formatearMoneda(usd) + ' USD';

    spanPrecio.appendChild(spanMXN);
    spanPrecio.appendChild(spanUSD);
  }

  /* ── PRECIO DE CARRITO ───────────────────────────────────────
     Version mas compacta: solo agrega el "USD" al lado (sin la
     etiqueta "MXN", para no saturar el carrito). */
  function inyectarEnPrecioCarrito(el, tc) {
    if (el.querySelector('.sc-mxn')) return;   // ya inyectado, salir

    var mxn = parsearPrecio(el.textContent);
    if (isNaN(mxn) || mxn <= 0) return;
    var usd = mxn / tc;

    var spanUSD = document.createElement('span');
    spanUSD.className = 'sc-mxn';
    spanUSD.style.cssText = 'font-size: 0.8em; color: rgb(102, 102, 102); margin-left: 4px;';
    spanUSD.textContent = formatearMoneda(usd) + ' USD';

    el.appendChild(spanUSD);
  }

  /* Funcion central: recorre todos los precios de la pagina y les
     inyecta el USD. Primero se asegura de tener el tipo de cambio
     (esperando la promesa), y luego procesa producto/grid y carrito. */
  function procesarPrecios() {
    obtenerTipoCambio().then(function (tc) {
      // 1) Precios de producto y grid de coleccion.
      document.querySelectorAll('.price-item--regular').forEach(function (el) {
        if (el.textContent.trim().length > 0 && /\d/.test(el.textContent)) {
          inyectarEnPrecioProducto(el, tc);
        }
      });

      // 2) Precios del carrito. Estos SELECTORES dependen de las
      //    clases del tema; si una actualizacion del tema cambia
      //    las clases de los precios del carrito, hay que
      //    actualizarlos aqui (ver guia de recuperacion).
      var selectoresCarrito = [
        '.cart-item__final-price',      // drawer lateral: precio de la linea
        '.cart-item__prices .price',    // /cart: precio unitario
        '.cart-item__totals .price',    // /cart: total de la linea
        '.totals__subtotal-value'       // subtotal (drawer y /cart)
      ];
      document.querySelectorAll(selectoresCarrito.join(',')).forEach(function (el) {
        if (el.querySelector('.sc-mxn')) return;
        if (el.textContent.trim().length > 0 && /\d/.test(el.textContent)) {
          inyectarEnPrecioCarrito(el, tc);
        }
      });
    });
  }

  /* Ejecuta al cargar la pagina. */
  document.addEventListener('DOMContentLoaded', procesarPrecios);

  /* ── REPROCESO AUTOMATICO ────────────────────────────────────
     El carrito (drawer, /cart) se regenera dinamicamente al
     agregar/quitar productos. Para volver a inyectar el USD en los
     precios nuevos, se reprocesa ante varios disparadores, con un
     pequeno "debounce" (espera de 120 ms) para no recalcular de mas. */
  var debounceTimer = null;
  function procesarConDebounce() {
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(procesarPrecios, 120);
  }

  // (a) Observa cambios en todo el <body>: cubre grid, /cart y el
  //     drawer que se regenera.
  new MutationObserver(procesarConDebounce)
    .observe(document.body, { childList: true, subtree: true });

  // (b) Eventos que dispara el tema al actualizar el carrito.
  document.addEventListener('cart:refresh', procesarConDebounce);
  document.addEventListener('cart:updated', procesarConDebounce);
  document.addEventListener('cart-drawer:open', procesarConDebounce);

  // (c) Respaldo: interceptamos las llamadas fetch a las rutas de
  //     carrito de Shopify (/cart/add|change|update|clear) y
  //     reprocesamos tras ellas. Cubre casos que los eventos (b)
  //     no alcancen.
  if (window.fetch) {
    var _fetch = window.fetch;
    window.fetch = function () {
      return _fetch.apply(this, arguments).then(function (res) {
        try {
          var url = (arguments[0] && arguments[0].url) || arguments[0] || '';
          if (typeof url === 'string' && /\/cart\/(add|change|update|clear)/.test(url)) {
            procesarConDebounce();
          }
        } catch (e) { /* silencioso */ }
        return res;
      });
    };
  }
})();
